import { useNavigate } from 'react-router-dom'
import { DOMAIN_LABELS } from '@/types/contextEngine'
import { DOMAIN_ICONS } from './domainIcons'
import type { DomainScore } from '@/types/contextEngine'

interface DomainCardProps {
  score: DomainScore
}

function daysAgoLabel(days: number): string {
  if (days <= 0) return 'Updated today'
  if (days === 1) return 'Updated yesterday'
  if (days < 7) return `Updated ${days}d ago`
  if (days < 30) return `Updated ${Math.floor(days / 7)}w ago`
  return `Updated ${Math.floor(days / 30)}mo ago`
}

export function DomainCard({ score }: DomainCardProps) {
  const navigate = useNavigate()
  const Icon = DOMAIN_ICONS[score.domain]
  const label = DOMAIN_LABELS[score.domain]
  const pct = Math.round(score.score)
  const topGap = score.gaps[0]

  return (
    <button
      onClick={() => navigate(`/context/domains/${score.domain}`)}
      className="flex flex-col gap-3 p-4 rounded-lg bg-bg-secondary border border-border hover:border-accent-amber/30 hover:bg-bg-secondary/80 transition-all duration-fast text-left group"
    >
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-md bg-bg-tertiary flex items-center justify-center group-hover:bg-accent-amber/10 transition-colors">
          <Icon size={16} className="text-text-tertiary group-hover:text-accent-amber transition-colors" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-sm font-semibold text-text-primary truncate">{label}</div>
        </div>
        <span className="font-mono text-xs text-text-tertiary">{pct}%</span>
      </div>

      {/* Progress bar */}
      <div className="w-full h-1.5 rounded-full bg-bg-tertiary overflow-hidden">
        <div
          className="h-full rounded-full bg-accent-amber transition-all duration-slow"
          style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
        />
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-xs">
        <span className="font-mono text-text-tertiary">
          {score.activeChunks} chunk{score.activeChunks !== 1 ? 's' : ''}
        </span>
        {score.activeChunks > 0 ? (
          <span className="text-text-tertiary">{daysAgoLabel(score.oldestChunkDays)}</span>
        ) : (
          <span className="text-accent-amber/70">No data yet</span>
        )}
      </div>

      {/* Top gap */}
      {topGap && (
        <div className="text-xs text-accent-amber/80 truncate -mt-1">
          Gap: {topGap}
        </div>
      )}
    </button>
  )
}
